import React, { useMemo, useState } from "react"

import { Calendar, ClipboardList, Phone, Plus } from "lucide-react"
import { AlertCircle } from "lucide-react"

import DiaryEntryCard from "@/components/people/diary/DiaryEntryCard"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Spinner } from "@/components/ui/spinner"
import { useAuth } from "@/contexts/AuthContext"
import { useFetchDiaryEntries } from "@/hooks/useFetchDiaryEntries"
import { useUpdateDiaryEntry } from "@/hooks/useUpdateDiaryEntry"
import { DiaryEntry, Person } from "@/types/person"

import AddDiaryEntryDialog from "../dialogs/AddDiaryEntryDialog"

interface DiaryEntriesTabProps {
  person: Person
}

const DiaryEntriesTab: React.FC<DiaryEntriesTabProps> = ({ person }) => {
  const [showAddDialog, setShowAddDialog] = useState(false)
  const { claims } = useAuth()

  const teamPermissions = claims.team_permissions || []
  const canEditDiary = teamPermissions.includes("update.tab.diary")

  // Fetch diary entries only when tab is active
  const { data, isLoading, error } = useFetchDiaryEntries({
    personId: person.id, // Pass UUID directly
    // Only enabled when tab is active
  })

  const updateDiaryEntryMutation = useUpdateDiaryEntry()

  // Handle confirming completion of a follow-up action
  const handleConfirmComplete = (entry: DiaryEntry) => {
    updateDiaryEntryMutation.mutate({
      id: entry.id, // Supabase UUID
      followUpComplete: true,
    })
  }

  const diaryEntries = useMemo(() => data?.data || [], [data?.data])

  // Calculate last call date/time and days since last call from diary entries
  const lastCallInfo = React.useMemo(() => {
    if (diaryEntries.length === 0) {
      return {
        lastCallDateTime: null,
        daysSinceLastCall: null,
      }
    }

    // Find the most recent call date/time
    const sortedEntries = [...diaryEntries].sort((a, b) => {
      const dateA = new Date(a.callDateTime).getTime()
      const dateB = new Date(b.callDateTime).getTime()
      return dateB - dateA // Sort descending (most recent first)
    })

    const lastCallDateTime = sortedEntries[0].callDateTime
    const lastCallDate = new Date(lastCallDateTime)
    const now = new Date()
    const diffTime = now.getTime() - lastCallDate.getTime()
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    return {
      lastCallDateTime,
      daysSinceLastCall: diffDays,
    }
  }, [diaryEntries])

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime)
    return date.toLocaleString()
  }

  return (
    <div id="diary-entries-tab" className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Diary Entries</h3>
        {canEditDiary && (
          <Button size="sm" onClick={() => setShowAddDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Entry
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Call Log Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <Phone className="h-5 w-5 text-palette-jungleGreen600" />
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Last Call Date/Time
                </label>
                <p className="text-sm text-palette-jungleGreen900">
                  {lastCallInfo.lastCallDateTime
                    ? formatDateTime(lastCallInfo.lastCallDateTime)
                    : "No calls recorded"}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Calendar className="h-5 w-5 text-palette-jungleGreen600" />
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Days Since Last Call
                </label>
                <p className="text-sm text-palette-jungleGreen900">
                  {lastCallInfo.daysSinceLastCall !== null ? lastCallInfo.daysSinceLastCall : "N/A"}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading && (
        <div className="flex justify-center items-center py-8">
          <Spinner className="mr-2" />
          <span className="text-sm text-palette-jungleGreen600">Loading diary entries...</span>
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading diary entries: {error instanceof Error ? error.message : "Unknown error"}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        {!isLoading && diaryEntries.length > 0 ? (
          diaryEntries.map((entry) => (
            <DiaryEntryCard
              key={entry.id}
              entry={entry}
              onResolve={handleConfirmComplete}
              pendingUpdate={
                updateDiaryEntryMutation.isPending &&
                updateDiaryEntryMutation.variables?.id === entry.id
              }
              compactSpacing={true}
            />
          ))
        ) : (
          <Card>
            <CardContent className="text-center py-8">
              <ClipboardList className="h-12 w-12 text-palette-jungleGreen400 mx-auto mb-4" />
              <p className="text-palette-jungleGreen500">No diary entries recorded yet.</p>
            </CardContent>
          </Card>
        )}
      </div>

      <AddDiaryEntryDialog person={person} open={showAddDialog} onOpenChange={setShowAddDialog} />
    </div>
  )
}

export default DiaryEntriesTab
